// wrinkleReductionsData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[] | any;
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const wrinkleReductions: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "",
        "meta": {
            "title": "Best Wrinkle Reduction Treatment Dubai (FREE Consultation) | Elite Body Home",
            "description": "Professional wrinkle reduction treatment in Dubai with FREE consultation. Our experts provide safe & effective solutions for fine lines & wrinkles. Book your appointment today!",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/wrinkle-reduction/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "Best Wrinkle Reduction Treatment Dubai (FREE Consultation) | Elite Body Home",
                "description": "Professional wrinkle reduction treatment in Dubai with FREE consultation. Our experts provide safe & effective solutions for fine lines & wrinkles. Book your appointment today!",
                "url": "https://elitebodyhome.com/services/wrinkle-reduction/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:54:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Wrinkle-Reduction-Treatment.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Wrinkle-Reduction-Treatment.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "Wrinkle Reduction Treatment Dubai",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "Best Wrinkle Reduction Treatment Dubai (FREE Consultation) | Elite Body Home",
                "description": "Professional wrinkle reduction treatment in Dubai with FREE consultation. Our experts provide safe & effective solutions for fine lines & wrinkles. Book your appointment today!",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Wrinkle-Reduction-Treatment.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Wrinkle Reduction"],
        "hero": {
            "title": "Wrinkle Reduction",
            "subtitle": "",
            "description": "Wrinkle reduction treatments are essential for anyone looking to restore youthful skin by reducing the appearance of fine lines and wrinkles. These non-invasive or minimally invasive procedures help rejuvenate the skin, promoting a smooth and vibrant complexion. At Elite Body Home Polyclinic, we specialize in effective and safe wrinkle reduction treatments tailored to individual skin needs."
        },
        "howItWorks": {
            "title": "Wrinkle Reduction Procedure",
            "description": "What specific steps does Elite Body Home Polyclinic take in the wrinkle reduction process? We offer customized treatments using advanced techniques to deliver optimal results."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "Consultation & Skin Assessment",
                    "desc": "The specialist will assess your skin, wrinkle depth, and areas of concern to recommend the best treatment options.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "Preparation",
                    "desc": "The treatment area will be thoroughly cleansed and a numbing cream may be applied for comfort.",
                    "icon": "fa-solid fa-bubbles"
                },
                {
                    "title": "Botox Injections",
                    "desc": "Small amounts of botulinum toxin are injected to relax muscles and smooth overlying skin.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Dermal Fillers",
                    "desc": "Hyaluronic acid or other fillers are injected to plump and smooth areas with deep wrinkles.",
                    "icon": "fa-solid fa-prescription-bottle-medical"
                },
                {
                    "title": "Microneedling",
                    "desc": "Tiny needles create micro-injuries to stimulate natural collagen production.",
                    "icon": "fa-solid fa-needle"
                },
                {
                    "title": "Post-Treatment Care",
                    "desc": "Avoid touching treated areas and follow our aftercare instructions for optimal results.",
                    "icon": "fa-solid fa-hand-holding-medical"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits of Wrinkle Reduction",
            "subheading": "Why effective wrinkle reduction is beneficial",
            "para": "Wrinkle reduction treatments provide numerous advantages for those seeking younger-looking skin. Here's how our treatments can transform your appearance:",
            "items": [
                {
                    "title": "Smooth & Rejuvenated Skin",
                    "desc": "Effectively smooths out fine lines and deep wrinkles, restoring a youthful appearance.",
                    "icon": "fa-solid fa-face-grin"
                },
                {
                    "title": "Increased Collagen Production",
                    "desc": "Stimulates collagen which is vital for maintaining skin firmness and elasticity.",
                    "icon": "fa-solid fa-arrow-up"
                },
                {
                    "title": "Non-Invasive Options",
                    "desc": "Minimally invasive procedures offer fast recovery with minimal discomfort.",
                    "icon": "fa-solid fa-shield"
                },
                {
                    "title": "Improved Skin Texture",
                    "desc": "Enhances skin texture and tone by promoting cell turnover and collagen production.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "Boosted Confidence",
                    "desc": "Reduced wrinkles lead to a refreshed appearance, increasing self-esteem.",
                    "icon": "fa-solid fa-heart"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "",
            "para": "Wrinkle reduction treatments are suitable for various individuals looking to improve their skin's appearance. Here are some of the best candidates:",
            "items": [
                {
                    "title": "People with Mild to Moderate Wrinkles",
                    "desc": "Ideal for those with fine lines, wrinkles, or mild skin laxity seeking non-surgical solutions.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "Age Range 30s to 60s",
                    "desc": "Most beneficial for individuals in this age range, though treatments can be customized for others.",
                    "icon": "fa-solid fa-user-clock"
                },
                {
                    "title": "Good Overall Health",
                    "desc": "Candidates should be free from skin infections, certain autoimmune diseases, or pregnancy.",
                    "icon": "fa-solid fa-heart-pulse"
                }
            ]
        },
        "preparationTips": {
            "heading": "Prepare Before Wrinkle Reduction Treatment",
            "subheading": "",
            "para": "Proper preparation ensures your treatment is safe and effective. Follow these guidelines before your appointment:",
            "items": [
                {
                    "title": "Consultation",
                    "desc": "Discuss your skin goals and medical history with our experts during your consultation.",
                    "icon": "fa-solid fa-notes-medical"
                },
                {
                    "title": "Avoid Blood Thinners",
                    "desc": "Stop taking aspirin, ibuprofen and other blood-thinners for at least a week before treatment.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Sun Protection",
                    "desc": "Avoid excessive sun exposure before treatment to prevent complications.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "Hydrate",
                    "desc": "Drink plenty of water to keep skin hydrated and improve treatment quality.",
                    "icon": "fa-solid fa-bottle-water"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "After your wrinkle reduction treatment at Elite Body Home, you can expect:",
            "items": [
                {
                    "title": "Immediate Improvement",
                    "desc": "Botox and fillers show instant results with full effect visible within a week.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Gradual Enhancement",
                    "desc": "Laser and microneedling show progressive improvement as collagen production increases.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "Long-Lasting Effects",
                    "desc": "Results can last from several months to over a year depending on treatment type.",
                    "icon": "fa-solid fa-hourglass"
                },
                {
                    "title": "Natural-Looking Outcome",
                    "desc": "Our experts ensure results refresh your appearance while maintaining natural facial expressions.",
                    "icon": "fa-solid fa-face-laugh-beam"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How long do the results last?",
                    "answer": "The longevity varies by procedure. Botox results last 3-4 months, dermal fillers 6-18 months, while laser and microneedling stimulate long-term collagen production for more permanent improvements."
                },
                {
                    "question": "Is the treatment painful?",
                    "answer": "Most treatments involve minimal discomfort. Injections may cause slight stinging, while laser and microneedling feel like mild pricking. Topical anesthetics ensure your comfort throughout the procedure."
                },
                {
                    "question": "Are there any side effects?",
                    "answer": "Side effects are typically mild and temporary, including slight redness, swelling, or bruising at the treatment site that subsides within a few days."
                },
                {
                    "question": "How soon can I see results?",
                    "answer": "Botox shows effects in 3-7 days, fillers provide immediate results, while collagen-stimulating treatments like microneedling show gradual improvement over several weeks."
                },
                {
                    "question": "Which treatment is best for me?",
                    "answer": "Our specialists will recommend the optimal treatment after evaluating your skin type, wrinkle depth, and desired results during your consultation."
                },
                {
                    "question": "Is there any downtime?",
                    "answer": "Most treatments require minimal to no downtime. You may experience slight redness that typically fades within hours to a couple of days."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Wrinkle-Reduction-in-dubai-uae-2-scaled.jpg",
            "beforeAfter": [
                { "src": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Wrinkle-Reduction-in-dubai-1.jpg", "alt": "wrinkle reduction before after" },
                { "src": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Wrinkle-Reduction-in-dubai-2.jpg", "alt": "wrinkle treatment results" },
                { "src": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Wrinkle-Reduction-in-dubai-3.jpg", "alt": "anti-aging treatment results" }
            ]
        },
        "product": {
            "id": 312,
            "title": "Wrinkle Reduction Package",
            "price": 899,
            "description": "Comprehensive wrinkle reduction package including consultation, treatment sessions, and follow-up care. Our expert team offers effective solutions tailored to your skin needs."
        }
    },
    "ar": {
        "url": "",
        "meta": {
            "title": "أفضل علاج لتقليل التجاعيد في دبي (استشارة مجانية) | ايليت بودي هوم",
            "description": "علاج احترافي لتقليل التجاعيد في دبي مع استشارة مجانية. يقدم خبراؤنا حلولاً آمنة وفعالة للخطوط الدقيقة والتجاعيد. احجز موعدك اليوم!",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/ar/services/wrinkle-reduction/",
            "og": {
                "locale": "ar_AR",
                "type": "article",
                "title": "أفضل علاج لتقليل التجاعيد في دبي (استشارة مجانية) | ايليت بودي هوم",
                "description": "علاج احترافي لتقليل التجاعيد في دبي مع استشارة مجانية. يقدم خبراؤنا حلولاً آمنة وفعالة للخطوط الدقيقة والتجاعيد. احجز موعدك اليوم!",
                "url": "https://elitebodyhome.com/ar/services/wrinkle-reduction/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:54:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Wrinkle-Reduction-Treatment.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Wrinkle-Reduction-Treatment.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "علاج تقليل التجاعيد دبي",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "أفضل علاج لتقليل التجاعيد في دبي (استشارة مجانية) | ايليت بودي هوم",
                "description": "علاج احترافي لتقليل التجاعيد في دبي مع استشارة مجانية. يقدم خبراؤنا حلولاً آمنة وفعالة للخطوط الدقيقة والتجاعيد. احجز موعدك اليوم!",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Wrinkle-Reduction-Treatment.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "تقليل التجاعيد"],
        "hero": {
            "title": "تقليل التجاعيد",
            "subtitle": "",
            "description": "يعد علاج تقليل التجاعيد ضروري لكل من يرغب في بشرة أكثر نعومة ونضارة، من خلال التخفيف من مظهر الخطوط الدقيقة والتجاعيد. يشمل العلاج خيارات غير جراحية أو طفيفة التوغل، تساعد على تجديد البشرة وتعزيز إشراقتها. نقدم في عيادات ايليت بودي هوم باقة متنوعة من أنواع العلاج المصممة لتناسب احتياجات كل بشرة."
        },
        "howItWorks": {
            "title": "إجراء تقليل التجاعيد",
            "description": "ما هي الخطوات التي تتبعها عيادات ايليت بودي هوم في عملية تقليل التجاعيد؟ نقدم علاجات مخصصة باستخدام تقنيات متقدمة لتحقيق أفضل النتائج."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "الاستشارة وتقييم البشرة",
                    "desc": "يبدأ الإجراء باستشارة طبية يتم خلالها تقييم حالة البشرة، وعمق التجاعيد، والمناطق التي تحتاج إلى علاج.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "التحضير",
                    "desc": "تُنظف منطقة العلاج جيداً لإزالة أي آثار للمكياج أو الزيوت أو الشوائب.",
                    "icon": "fa-solid fa-bubbles"
                },
                {
                    "title": "حقن البوتوكس",
                    "desc": "تُحقن كميات صغيرة من مادة البوتوكس في العضلات المستهدفة، مما يؤدي إلى ارتخائها وتنعيم الجلد فوقها.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "حقن الفيلر",
                    "desc": "يُحقن حمض الهيالورونيك أو مواد الفيلر الأخرى في المناطق التي تعاني من تجاعيد عميقة أو فقدان الحجم، لملء الجلد وتنعيمه.",
                    "icon": "fa-solid fa-prescription-bottle-medical"
                },
                {
                    "title": "الميكرونيدلينغ",
                    "desc": "تُستخدم إبر دقيقة جداً لإحداث جروح دقيقة في الجلد، مما يحفز إنتاج الكولاجين الطبيعي ويمنح البشرة ملمساً أكثر نعومة.",
                    "icon": "fa-solid fa-needle"
                },
                {
                    "title": "العناية بعد العلاج",
                    "desc": "تجنب لمس المناطق المعالجة والتزم بتعليمات العناية المقدمة من العيادة.",
                    "icon": "fa-solid fa-hand-holding-medical"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد تقليل التجاعيد",
            "subheading": "لماذا يعتبر تقليل التجاعيد مفيدًا",
            "para": "توفر طرق تقليل التجاعيد العديد من المزايا لمن يسعون إلى بشرة تبدو أكثر شبابًا. إليك كيف يمكن لعلاجاتنا أن تحسن مظهرك:",
            "items": [
                {
                    "title": "بشرة ناعمة ومجددة",
                    "desc": "يعمل العلاج بفاعلية على تنعيم الخطوط الدقيقة والتجاعيد العميقة، مما يُعيد للبشرة مظهرها الشاب.",
                    "icon": "fa-solid fa-face-grin"
                },
                {
                    "title": "تحفيز إنتاج الكولاجين",
                    "desc": "تحفز العلاجات إنتاج الكولاجين الضروري للحفاظ على تماسك البشرة ومرونتها.",
                    "icon": "fa-solid fa-arrow-up"
                },
                {
                    "title": "خيارات غير جراحية",
                    "desc": "إجراءات طفيفة التوغل توفر تعافيًا سريعًا مع أقل قدر من الانزعاج.",
                    "icon": "fa-solid fa-shield"
                },
                {
                    "title": "تحسين ملمس البشرة",
                    "desc": "تحسن ملمس البشرة وتوحيد لونها من خلال تحفيز تجدد الخلايا وتعزيز إنتاج الكولاجين.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "تعزيز الثقة بالنفس",
                    "desc": "تؤدي النتائج الملحوظة إلى مظهر أكثر انتعاشاً وشباباً، مما يرفع من مستوى الثقة بالنفس.",
                    "icon": "fa-solid fa-heart"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية",
            "subheading": "",
            "para": "يناسب علاج تقليل التجاعيد لشريحة واسعة من الأشخاص الذين يسعون لتحسين مظهر بشرتهم ومكافحة علامات التقدم في العمر. إليك بعض الفئات التي يمكنها الاستفادة من العلاج:",
            "items": [
                {
                    "title": "الأشخاص ذوي التجاعيد الخفيفة إلى المتوسطة",
                    "desc": "مثالي لمن يعانون من خطوط دقيقة، تجاعيد، أو ترهلات بسيطة في الجلد يبحثون عن حلول غير جراحية.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "الفئة العمرية من 30 إلى 60 سنة",
                    "desc": "الأكثر استفادة هم الأفراد في هذا النطاق العمري، مع إمكانية تخصيص العلاج للفئات الأخرى.",
                    "icon": "fa-solid fa-user-clock"
                },
                {
                    "title": "صحة عامة جيدة",
                    "desc": "يجب أن يكون المرشحون خاليين من التهابات جلدية، أمراض مناعية معينة، أو الحمل.",
                    "icon": "fa-solid fa-heart-pulse"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد لعلاج تقليل التجاعيد",
            "subheading": "",
            "para": "يضمن التحضير السليم أن يكون علاجك آمنًا وفعالًا. اتبع هذه الإرشادات قبل موعدك:",
            "items": [
                {
                    "title": "الاستشارة",
                    "desc": "ناقش أهدافك للبشرة وتاريخك الطبي مع خبرائنا خلال الاستشارة.",
                    "icon": "fa-solid fa-notes-medical"
                },
                {
                    "title": "تجنب مميعات الدم",
                    "desc": "توقف عن تناول الأسبرين، الإيبوبروفين والأدوية المميعة للدم لمدة أسبوع على الأقل قبل العلاج.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "الحماية من الشمس",
                    "desc": "تجنب التعرض المفرط للشمس قبل العلاج لمنع المضاعفات.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "ترطيب الجسم",
                    "desc": "اشرب كميات كافية من الماء للحفاظ على ترطيب البشرة وتحسين جودة العلاج.",
                    "icon": "fa-solid fa-bottle-water"
                }
            ]
        },
        "results": {
            "heading": "النتائج",
            "subheading": "",
            "para": "بعد علاج تقليل التجاعيد في ايليت بودي هوم، يمكنك توقع:",
            "items": [
                {
                    "title": "تحسن فوري",
                    "desc": "يظهر البوتوكس والفيلر نتائج فورية مع اكتمال التأثير خلال أسبوع.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "تحسن تدريجي",
                    "desc": "يظهر العلاج بالليزر والميكرونيدلينغ تحسنًا تدريجيًا مع زيادة إنتاج الكولاجين.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "نتائج طويلة الأمد",
                    "desc": "يمكن أن تدوم النتائج من عدة أشهر إلى أكثر من عام حسب نوع العلاج.",
                    "icon": "fa-solid fa-hourglass"
                },
                {
                    "title": "نتائج طبيعية المظهر",
                    "desc": "يحرص خبراؤنا على أن تعطي النتائج مظهرًا منعشًا مع الحفاظ على تعابير الوجه الطبيعية.",
                    "icon": "fa-solid fa-face-laugh-beam"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "كم تدوم نتائج العلاج؟",
                    "answer": "تختلف المدة حسب الإجراء. تدوم نتائج البوتوكس 3-4 أشهر، الفيلر 6-18 شهرًا، بينما يحفز الليزر والميكرونيدلينغ إنتاج الكولاجين طويل الأمد لتحسينات أكثر ديمومة."
                },
                {
                    "question": "هل العلاج مؤلم؟",
                    "answer": "معظم العلاجات تتضمن انزعاجًا بسيطًا. قد تسبب الحقن لسعة خفيفة، بينما يشبه الليزر والميكرونيدلينغ وخزًا خفيفًا. تُستخدم مخدرات موضعية لضمان راحتك خلال الإجراء."
                },
                {
                    "question": "هل هناك أي آثار جانبية؟",
                    "answer": "الآثار الجانبية عادة ما تكون خفيفة ومؤقتة، وتشمل احمرارًا أو تورمًا أو كدمات طفيفة في موقع العلاج تختفي خلال أيام قليلة."
                },
                {
                    "question": "متى يمكنني رؤية النتائج؟",
                    "answer": "يظهر البوتوكس تأثيره خلال 3-7 أيام، يعطي الفيلر نتائج فورية، بينما تظهر العلاجات المحفزة للكولاجين مثل الميكرونيدلينغ تحسنًا تدريجيًا على مدار عدة أسابيع."
                },
                {
                    "question": "ما هو العلاج الأفضل لي؟",
                    "answer": "سيوصي أخصائينا بالعلاج الأمثل بعد تقييم نوع بشرتك، عمق التجاعيد، والنتائج المرجوة خلال استشارتك."
                },
                {
                    "question": "هل هناك فترة نقاهة؟",
                    "answer": "معظم العلاجات تتطلب فترة نقاهة قصيرة أو لا تتطلب أي نقاهة. قد تعاني من احمرار خفيف يختفي عادة خلال ساعات إلى يومين."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    }
}